"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IpBillingOpenApiOperations = void 0;
/**
 * IP Billing Workstation operations — ADR-085, registered into the real OpenAPI
 * document by `backend/src/modules/ip/http/ip-billing.openapi.ts`.
 *
 * THIS REGISTRY DESCRIBES WHAT THE SERVER ACTUALLY SERVES. Documenting an
 * endpoint that answers 404 would put a method on the generated client that
 * cannot be called, and the caller would find out in production rather than at
 * compile time.
 *
 * WHY THIS FILE EXISTS. Phases 0-13 shipped ~60 live, tested IP endpoints with
 * ZERO OpenAPI registrations. A React workstation built on them would have been
 * hand-written against a markdown table — precisely the drift the
 * "generated, not handwritten" rule exists to prevent
 * (ENGINEERING-PRINCIPLES.md §3). This is the same gap Gate 17 finding F-1
 * recorded for OP billing, closed here before any React is written rather than
 * after.
 *
 * SCOPE: the operations the WORKSTATION needs — admission search and context,
 * the charge timeline and capture, pricing, interim billing, settlement,
 * posting, and the dashboard. The administrative surfaces (plan/exclusion
 * masters, pricing-policy CRUD, authority-matrix maintenance) are deliberately
 * absent: they are configuration screens, not workstation actions, and each is
 * added in the same commit that gives it a UI.
 */
exports.IpBillingOpenApiOperations = [
    // ---------------------------------------------------------------- admission
    {
        operationId: 'listIpAdmissions',
        method: 'GET',
        path: '/v1/admissions',
        summary: 'Search current inpatients for the workstation left panel',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'listAdmissionsQuerySchema',
        responseSchemaRef: 'IpAdmissionListPage',
        errorCodes: ['AUTH_UNAUTHENTICATED', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpAdmission',
        method: 'GET',
        path: '/v1/admissions/{id}',
        summary: 'Load one admission with its open bed occupancy and allowed transitions',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpAdmissionDetail',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // ------------------------------------------------------------------ charges
    {
        operationId: 'listIpCharges',
        method: 'GET',
        path: '/v1/admissions/{id}/charges',
        summary: 'The admission charge ledger',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChargeLedgerEntry[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'captureIpCharge',
        method: 'POST',
        path: '/v1/admissions/{id}/charges',
        summary: 'Capture a charge through the single gateway',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'captureIpChargeSchema',
        responseSchemaRef: 'IpChargeLedgerEntry',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    /*
      BATCH BRIDGE (desk, 2026-09-29) — see `IpLabBridgeService.bridgeBatch`'s
      own header. Finishes a "Save All" batch by turning every LAB line it
      captured into one laboratory order, the IP-side twin of the OP bridge's
      per-invoice grouping.
    */
    {
        operationId: 'bridgeIpChargeBatch',
        method: 'POST',
        path: '/v1/admissions/{id}/charges/bridge-batch',
        summary: 'Finish a Save All batch — group its LAB charges into one laboratory order',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'bridgeIpChargeBatchSchema',
        responseSchemaRef: 'unknown',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    /*
      CHARGE DRAFTS — the two stages before approval (desk, 2026-08-26).
  
      Registered here rather than left to a hand-written caller for the reason
      this whole file exists: a workstation built against an unregistered endpoint
      is a workstation built against a markdown table, and it drifts the first time
      the contract moves.
    */
    {
        operationId: 'listIpChargeDrafts',
        method: 'GET',
        path: '/v1/admissions/{id}/charges/drafts',
        summary: 'Charge lines typed against this stay but not yet approved',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChargeDraft[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'saveIpChargeDrafts',
        method: 'POST',
        path: '/v1/admissions/{id}/charges/drafts',
        summary: 'Keep a batch of draft lines against the stay',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'saveIpChargeDraftsSchema',
        responseSchemaRef: 'IpChargeDraft[]',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'markIpChargeDraftsSaved',
        method: 'POST',
        path: '/v1/admissions/{id}/charges/drafts/mark-saved',
        summary: 'Move draft lines to SAVED — kept deliberately, still correctable',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'markIpChargeDraftsSavedSchema',
        responseSchemaRef: 'IpChargeDraftsSavedResult',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'updateIpChargeDraft',
        method: 'PATCH',
        path: '/v1/admissions/{id}/charges/drafts/{draftId}',
        summary: 'Edit a draft or saved line — both stages are correctable',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'updateIpChargeDraftSchema',
        responseSchemaRef: 'IpChargeDraft',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'deleteIpChargeDraft',
        method: 'DELETE',
        path: '/v1/admissions/{id}/charges/drafts/{draftId}',
        summary: 'Discard a draft or saved line',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChargeDraftDeleted',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpUnapprovedCharges',
        method: 'GET',
        path: '/v1/admissions/{id}/charges/unapproved',
        summary: 'How many lines are still unapproved — what the discharge gate refuses on',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpUnapprovedChargeCounts',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpChargeSummary',
        method: 'GET',
        path: '/v1/admissions/{id}/charges/summary',
        summary: 'Server-computed running totals for the right rail',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChargeSummary',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /*
          The IP half of the Laboratory and Radiology registers. NOT scoped to an
          admission — the question is what one COUNTER billed across every stay it
          touched, which is why the path sits above `/{id}/...` on the router too.
        */
        operationId: 'getIpDeskChargeRegister',
        method: 'GET',
        path: '/v1/admissions/charges/desk-register',
        summary: 'What one desk (Lab, Radiology, …) captured onto admissions in a date window',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'IpDeskChargeRegisterQuery',
        responseSchemaRef: 'IpDeskChargeRegister',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        /*
          IP BILL STATUS (desk, 2026-09-30) — the register: every bill number that
          carries lines, saved and approved, condensed. Above `/{id}` for the same
          reason the desk register is.
        */
        operationId: 'getIpBillStatusRegister',
        method: 'GET',
        path: '/v1/admissions/charges/bill-status',
        summary: 'Every IP bill number with its saved and approved lines, condensed per bill',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'IpBillStatusQuery',
        responseSchemaRef: 'IpBillStatusRegister',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpBillStatusStatement',
        method: 'GET',
        path: '/v1/admissions/{id}/charges/bill-status',
        summary: 'One IP bill: condensed groups and every saved and approved line',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpBillStatusStatement',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /*
          WARD TRANSFER (desk, 2026-09-30) — the IP Admission page's Ward Transfer tab. The three fixed paths sit ABOVE
          `/{id}` in the router for the reason the bill-status register does; `ward-transfers` would otherwise be read as
          an admission id. Gated like the IP Admission page itself (`ip:admission:admit`) — see the route.
        */
        operationId: 'lookupIpWardTransferStay',
        method: 'GET',
        path: '/v1/admissions/ward-transfers/lookup',
        summary: 'Find a stay by its 6-digit IP bill number and show the ward the patient is in now',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'IpWardTransferLookupQuery',
        responseSchemaRef: 'IpWardTransferStay',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpWardTransferWards',
        method: 'GET',
        path: '/v1/admissions/ward-transfers/wards',
        summary: 'The wards a patient can be moved into, with their type',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpWardTransferWard',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpWardTransferRegister',
        method: 'GET',
        path: '/v1/admissions/ward-transfers',
        summary: 'Ward transfers by date and bill number: which bill, from and to which ward, who did it, and when',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'IpWardTransferRegisterQuery',
        responseSchemaRef: 'IpWardTransferRegister',
        errorCodes: ['GEN_VALIDATION_ERROR', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'createIpWardTransfer',
        method: 'POST',
        path: '/v1/admissions/{id}/ward-transfer',
        summary: 'Move a stay into another ward and record who did it',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'createIpWardTransferSchema',
        responseSchemaRef: 'IpWardTransferResult',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpChargeTimeline',
        method: 'GET',
        path: '/v1/admissions/{id}/charges/timeline',
        summary: 'Chronological admission timeline, satisfied from the ledger alone',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpTimelineEntry[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpChargesByGroup',
        method: 'GET',
        path: '/v1/admissions/{id}/charges/by-group',
        summary: 'Charges bucketed by charge group for the summary rail',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChargeGroupSummary[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'reverseIpCharge',
        method: 'POST',
        path: '/v1/admissions/{id}/charges/{ledgerId}/reverse',
        summary: 'Append a reversal; the original is never edited',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'reverseIpChargeSchema',
        responseSchemaRef: 'IpChargeLedgerEntry',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'AUTHZ_FORBIDDEN'],
    },
    // ------------------------------------------------------------------ pricing
    {
        operationId: 'searchIpServices',
        method: 'GET',
        path: '/v1/ip/services/search',
        summary: 'Keyboard-first Service Master search',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'ipServiceSearchQuerySchema',
        responseSchemaRef: 'IpServiceSearchResult[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'resolveIpPrice',
        method: 'POST',
        path: '/v1/admissions/{id}/resolve-price',
        summary: 'One call returns a complete billing-ready service; the client computes nothing',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'resolveIpPriceSchema',
        responseSchemaRef: 'ResolvedIpPrice',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'setIpChargeTier',
        method: 'PUT',
        path: '/v1/admissions/{id}/charge-tier',
        summary: "Record the stay's ward tier (C1-C4) without repricing anything",
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'setIpChargeTierSchema',
        responseSchemaRef: 'SetIpChargeTierResult',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpOtDoctors',
        method: 'GET',
        path: '/v1/admissions/ot-doctors',
        summary: 'Candidate doctors for an operating-team role; ANAESTHETIST narrows to the anaesthesia department',
        version: 'v1',
        tags: ['IP Billing'],
        // No request schema: the only input is the `role` query string, and there
        // is no zod object for it. Stated as an empty ref rather than omitted, so
        // the required field is answered deliberately.
        requestSchemaRef: '',
        responseSchemaRef: 'OtDoctorOption[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'applyIpWardTier',
        method: 'POST',
        path: '/v1/admissions/{id}/apply-tier',
        summary: 'Apply an operator-asserted ward tier (C1-C4) to every uninvoiced service on the admission',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'applyIpTierSchema',
        responseSchemaRef: 'TierRepriceResult',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // ------------------------------------------------------------ interim bills
    {
        operationId: 'listIpInterimBills',
        method: 'GET',
        path: '/v1/admissions/{id}/interim-bills',
        summary: 'Interim bill snapshots for this stay',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpInterimBillSnapshot[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'generateIpInterimBill',
        method: 'POST',
        path: '/v1/admissions/{id}/interim-bills',
        summary: 'Take a new immutable snapshot',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'generateInterimBillSchema',
        responseSchemaRef: 'IpInterimBillSnapshot',
        errorCodes: ['GEN_VALIDATION_ERROR', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'compareIpInterimBills',
        method: 'GET',
        path: '/v1/admissions/{id}/interim-bills/compare',
        summary: 'Delta between two snapshots — why today differs from yesterday',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'compareInterimBillsQuerySchema',
        responseSchemaRef: 'InterimBillComparison',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // ------------------------------------------------------------- settlement
    {
        operationId: 'validateIpSettlement',
        method: 'GET',
        path: '/v1/admissions/{id}/settlement/validate',
        summary: 'Every settlement blocker in one pass, each with a code',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpSettlementValidation',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpSettlements',
        method: 'GET',
        path: '/v1/admissions/{id}/settlements',
        summary: 'Settlements for this admission',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpFinalSettlementSnapshot[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'settleIpAdmission',
        method: 'POST',
        path: '/v1/admissions/{id}/settlements',
        summary: 'Create the legal invoice; both acceptance equations are asserted first',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'settleAdmissionSchema',
        responseSchemaRef: 'IpFinalSettlementSnapshot',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * ADVISING DISCHARGE — ADR-057's first lifecycle step. The endpoint has
         * existed since Phase 0 and nothing called it, which is half of why the
         * lifecycle collapsed to three states: with no path in the registry the
         * workstation had no typed client to reach it with.
         */
        operationId: 'adviseIpDischarge',
        method: 'POST',
        path: '/v1/admissions/{id}/discharge-advice',
        summary: 'Record discharge advice — ADMITTED to DISCHARGE_ADVISED',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'adviseDischargeSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * CANCEL DISCHARGE — the stay's second backward edge (desk, 2026-09-25).
         * DISCHARGED back to ADMITTED inside 48 hours of the discharge advice, so
         * further entries can be made. Changes nothing already posted.
         */
        operationId: 'cancelIpDischarge',
        method: 'POST',
        path: '/v1/admissions/{id}/cancel-discharge',
        summary: 'Cancel a discharge inside its 48-hour window — DISCHARGED to ADMITTED',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'cancelDischargeSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * CANCEL ADMISSION (desk, 2026-09-25) — ADMITTED to CANCELLED, refused if
         * any charge is already on the ledger. If an advance already exists this
         * does NOT reach CANCELLED: the server routes it to CANCELLATION_REQUESTED
         * instead, and only `approveIpAdmissionCancellation` can move it on. One
         * clerk action, one endpoint, two possible outcomes — see
         * `IpAdmissionApplicationService.cancel`'s own doc comment.
         */
        operationId: 'cancelIpAdmission',
        method: 'POST',
        path: '/v1/admissions/{id}/cancel',
        summary: 'Cancel an admission — direct if no advance exists yet, otherwise sent for approval',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'cancelAdmissionSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'approveIpAdmissionCancellation',
        method: 'POST',
        path: '/v1/admissions/{id}/cancel/approve',
        summary: 'Approve a cancellation request — CANCELLATION_REQUESTED to CANCELLED',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'approveCancellationSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'rejectIpAdmissionCancellation',
        method: 'POST',
        path: '/v1/admissions/{id}/cancel/reject',
        summary: 'Reject a cancellation request — CANCELLATION_REQUESTED back to ADMITTED',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'rejectCancellationSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * COST SHEET PROVISION (desk, 2026-09-27) — a plain approve/disable
         * switch, toggled by bill number from its own tab under IP Admission, at
         * any time in either direction. Not a lifecycle transition; works
         * regardless of the admission's own status. IP Drug Entries only offers a
         * bill number once this reads `true` — see `costSheetProvisionApproved`
         * on `listAdmissionsQuerySchema`.
         */
        operationId: 'setIpAdmissionCostSheetProvision',
        method: 'PATCH',
        path: '/v1/admissions/{id}/cost-sheet-provision',
        summary: 'Approve or disable Cost Sheet Provision for an admission',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'setCostSheetProvisionSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // IP DRUG ENTRIES (desk, 2026-09-25) — pharmacy bills entered against admitted
    // stays, and refunds against them. Registered so the page is built against the
    // typed client, not hand-written URLs.
    {
        operationId: 'createIpDrugEntry',
        method: 'POST',
        path: '/v1/admissions/{id}/drug-entries',
        summary: 'Enter a pharmacy bill against an ADMITTED stay — captures one Drugs charge',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'createIpDrugEntrySchema',
        responseSchemaRef: 'IpDrugEntry',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpAdmissionDrugEntries',
        method: 'GET',
        path: '/v1/admissions/{id}/drug-entries',
        summary: "One stay's drug bills, with the server's totals",
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpDrugEntryList',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpChangeovers',
        method: 'GET',
        path: '/v1/admissions/{id}/changeovers',
        summary: 'Every payer changeover raised against this stay, newest first',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChangeoverList',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'createIpChangeover',
        method: 'POST',
        path: '/v1/admissions/{id}/changeovers',
        summary: 'Raise a payer changeover (self-pay <-> insurance) — computes nothing, moves no money',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'createIpChangeoverSchema',
        responseSchemaRef: 'IpChangeover',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpChangeover',
        method: 'GET',
        path: '/v1/admissions/{id}/changeovers/{changeoverId}',
        summary: 'One changeover with its analysis lines (latest version unless one is named)',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'getIpChangeoverQuerySchema',
        responseSchemaRef: 'IpChangeoverDetail',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'analyzeIpChangeover',
        method: 'POST',
        path: '/v1/admissions/{id}/changeovers/{changeoverId}/analyze',
        summary: 'Run (or re-run) the read-only analysis — every earlier version is kept',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChangeoverAnalysis',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'approveIpChangeover',
        method: 'POST',
        path: '/v1/admissions/{id}/changeovers/{changeoverId}/approve',
        summary: 'Approve an analysed changeover',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpChangeover',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'rejectIpChangeover',
        method: 'POST',
        path: '/v1/admissions/{id}/changeovers/{changeoverId}/reject',
        summary: 'Reject a changeover that has not been applied',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'rejectIpChangeoverSchema',
        responseSchemaRef: 'IpChangeover',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'cancelIpChangeover',
        method: 'POST',
        path: '/v1/admissions/{id}/changeovers/{changeoverId}/cancel',
        summary: 'Withdraw a changeover that has not been applied',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'cancelIpChangeoverSchema',
        responseSchemaRef: 'IpChangeover',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'applyIpChangeover',
        method: 'POST',
        path: '/v1/admissions/{id}/changeovers/{changeoverId}/apply',
        summary: 'Apply an approved changeover — on a settled stay, reverses and re-posts it atomically',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'applyIpChangeoverSchema',
        responseSchemaRef: 'IpChangeoverApplyResult',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpDrugEntries',
        method: 'GET',
        path: '/v1/admissions/drug-entries',
        summary: 'The drug-entry register across stays',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'listIpDrugEntriesQuerySchema',
        responseSchemaRef: 'IpDrugEntryRegister',
        errorCodes: ['GEN_VALIDATION_ERROR', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'refundIpDrugEntry',
        method: 'POST',
        path: '/v1/admissions/drug-entries/{entryId}/refunds',
        summary: 'Refund all or part of a drug bill — writes a negative Drugs charge',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'refundIpDrugEntrySchema',
        responseSchemaRef: 'IpDrugEntry',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /** Every discharge and every cancellation on the stay, oldest first. */
        operationId: 'listIpDischargeHistory',
        method: 'GET',
        path: '/v1/admissions/{id}/discharge-history',
        summary: 'Discharge and discharge-cancellation history for this stay',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpDischargeEvent[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * ENTERING CLEARANCE — the state a stay waits in while pharmacy returns,
         * investigations and the bill are chased. It is the LAST state in which a
         * charge can still be added, which is the whole reason it sits before
         * settlement rather than after it.
         */
        operationId: 'enterIpClearance',
        method: 'POST',
        path: '/v1/admissions/{id}/clearance',
        summary: 'Enter clearance — DISCHARGE_ADVISED to PENDING_CLEARANCE',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'enterClearanceSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * WITHDRAWING A CLEARANCE — the lifecycle's one backward edge, for a
         * patient who deteriorates while waiting to go home. The reason is
         * mandatory; a backward transition with no explanation is unauditable.
         */
        operationId: 'revokeIpClearance',
        method: 'POST',
        path: '/v1/admissions/{id}/clearance/revoke',
        summary: 'Withdraw a clearance — PENDING_CLEARANCE back to ADMITTED',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'revokeClearanceSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        /**
         * COMPLETING THE DISCHARGE — live and permission-gated since Phase 0, but
         * never registered, so the spec described a module that could settle a
         * stay and never close one. The workstation's Discharge action is what
         * needed it: without the path here the only way to call it would be a
         * hand-written URL, which is the drift this registry exists to prevent.
         *
         * It is a SEPARATE operation from settling on purpose. Settlement creates
         * the invoice; this ends the stay, and the desk does not reach it until
         * the money is clear.
         */
        operationId: 'completeIpDischarge',
        method: 'POST',
        path: '/v1/admissions/{id}/discharge',
        summary: 'End the stay once it is settled and the balance is clear',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'completeDischargeSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // ------------------------------------------------------------ advances
    // The ward desk's own money: what has been taken against the stay, what is
    // still owed after it, and taking one. Declared here so the generated client
    // knows these paths — an endpoint the spec does not carry is one the frontend
    // cannot call without hand-writing a URL, which is what the generator exists
    // to prevent.
    {
        operationId: 'listIpDeposits',
        method: 'GET',
        path: '/v1/admissions/{id}/deposits',
        summary: 'Advances taken against this stay',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpAdmissionDeposit[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpAdvancePosition',
        method: 'GET',
        path: '/v1/admissions/{id}/advance-position',
        summary: 'What is still outstanding after the advances taken — computed server-side',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpAdvancePosition',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'recordIpAdvance',
        method: 'POST',
        path: '/v1/admissions/{id}/advances',
        summary: 'Take an advance: collect it at the counter, post the deposit, issue the numbered receipt',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'recordIpAdvanceSchema',
        responseSchemaRef: 'IpAdmissionDeposit',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpAdvanceReceipt',
        method: 'GET',
        path: '/v1/admissions/{id}/advances/{depositId}/receipt',
        summary: 'The advance receipt again, rebuilt from stored facts — allocates no new number',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpAdvanceReceipt',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'postIpSettlement',
        method: 'POST',
        path: '/v1/admissions/{id}/settlements/{settlementId}/post',
        summary: 'Post to the General Ledger; unbalanced journals are rejected',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpPostSettlementResult',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpSettlementIntegrity',
        method: 'GET',
        path: '/v1/ip/settlements/{settlementId}/integrity',
        summary: 'The three-state financial integrity verdict',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpIntegrityVerdict',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    // -------------------------------------------------------------- dashboard
    {
        operationId: 'getIpDashboard',
        method: 'GET',
        path: '/v1/ip/dashboard',
        summary: 'The command centre in one aggregated call',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpDashboardSnapshot',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    // ---------------------------------------------------------------- printing
    {
        operationId: 'printIpDocument',
        method: 'POST',
        path: '/v1/ip/documents/print',
        summary: 'Render an immutable snapshot to PDF with a signed verification QR',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'printIpDocumentSchema',
        responseSchemaRef: 'application/pdf',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // ------------------------------------------------------- registration desk
    //
    // The admission-CAPTURE screen, added after the Phase 15 freeze. Phase 15
    // forbade new endpoints during HARDENING — it did not freeze the module
    // permanently, and `ip-api-surface-freeze.test.ts` documents updating its
    // snapshot as the deliberate, reviewable way to move the surface. That is
    // what this block does, rather than sliding under the freeze test's path
    // filter (which matched `/ip/` and `/admissions`, and would have missed
    // `/ip-registration/*` entirely — the filter is widened in the same change).
    {
        operationId: 'admitPatient',
        method: 'POST',
        path: '/v1/admissions',
        summary: 'Admit a patient from the IP registration desk',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'admitPatientSchema',
        responseSchemaRef: 'IpAdmission',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_CONFLICT', 'GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'lookupIpPatientByUhid',
        method: 'GET',
        path: '/v1/ip-registration/lookup',
        summary: 'Resolve a UHID to the patient, their running admission and their IP/bill history',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'ipUhidLookupQuerySchema',
        responseSchemaRef: 'IpUhidLookupResponse',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpClinicalDepartments',
        method: 'GET',
        path: '/v1/ip-registration/departments',
        summary: 'Clinical departments only — the department picker',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpRegistrationDepartment[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpAdmittableWards',
        method: 'GET',
        path: '/v1/ip-registration/wards',
        summary: 'Wards currently accepting admissions',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpRegistrationWard[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpRegistrationDoctors',
        method: 'GET',
        path: '/v1/ip-registration/doctors',
        summary: 'Active doctors, keyed by User id, for all three doctor pickers',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpRegistrationDoctor[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpRegistrationPayers',
        method: 'GET',
        path: '/v1/ip-registration/payers',
        summary: 'Payers for COMPANY or INSURANCE credit',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'ipRegistrationPayerQuerySchema',
        responseSchemaRef: 'IpRegistrationPayer[]',
        errorCodes: ['GEN_VALIDATION_ERROR', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'listIpPatientPolicies',
        method: 'GET',
        path: '/v1/ip-registration/patients/{patientId}/policies',
        summary: "The patient's own active insurance policies",
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpRegistrationPolicy[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'captureIpPatientAadhaar',
        method: 'POST',
        path: '/v1/ip-registration/patients/{patientId}/aadhaar',
        summary: 'Record an Aadhaar deferred at admission, or correct the one on file',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'ipCaptureAadhaarSchema',
        responseSchemaRef: 'IpCaptureAadhaarResponse',
        errorCodes: ['GEN_VALIDATION_ERROR', 'GEN_NOT_FOUND', 'GEN_CONFLICT', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'searchIpRegistrationEmployees',
        method: 'GET',
        path: '/v1/ip-registration/employees',
        summary: 'Employees for EMPLOYEE credit',
        version: 'v1',
        tags: ['IP Registration'],
        requestSchemaRef: 'ipRegistrationEmployeeQuerySchema',
        responseSchemaRef: 'IpRegistrationEmployee[]',
        errorCodes: ['AUTHZ_FORBIDDEN'],
    },
    // -------------------------------------------------------------- concessions
    /*
     * ADR-085 Phase 7's discount routes have been LIVE and permission-gated since
     * they were built, but never registered here — so the workstation's
     * Concession tab (desk, 2026-09-10) had no typed client to read them with.
     * Both are read-only; the request/approve/reject writes stay unregistered
     * until a screen actually raises a concession from this workstation.
     */
    {
        operationId: 'listIpConcessions',
        method: 'GET',
        path: '/v1/admissions/{id}/discounts',
        summary: 'Every concession raised against this stay, approved or not',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpConcessionRow[]',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    {
        operationId: 'getIpConcessionSummary',
        method: 'GET',
        path: '/v1/admissions/{id}/discounts/summary',
        summary: 'Patient liability before concession, the approved total, and the net payable',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'none',
        responseSchemaRef: 'IpConcessionSummary',
        errorCodes: ['GEN_NOT_FOUND', 'AUTHZ_FORBIDDEN'],
    },
    // ----------------------------------------------------------------- reports
    /*
     * ADR-085 Phase 11's reporting framework (twelve reports, then fourteen —
     * desk, 2026-09-15's "Ward Bills Made on Date" / "Discharges Advised on
     * Date") has been live behind `POST /ip/reports/{reportKey}/run` since
     * Phase 11, reachable only from dashboard drill-throughs with hand-built
     * fetches. Registered here so the two new desk registers can be built
     * against the generated client instead — `reportKey` is a REGISTRY KEY
     * ('ip-ward-bills-made'), not a UUID, which is why it needs its own
     * path-param schema (see `pathParamsSchema`'s special case in
     * `ip-billing.openapi.ts`).
     *
     * THE PATH HAS THE `/ip` SEGMENT because `ip-report.controller.ts`'s routes
     * are registered on `ipPricingRouter`, and `routes/v1/index.ts` mounts that
     * router at `v1Router.use('/ip', ipPricingRouter)` — the same prefix every
     * other operation in this router carries (`/ip/services/search`, `/ip/
     * dashboard`, `/ip/settlements/{id}/integrity`). This entry omitted it
     * (2026-09-15) and every desk register call 404'd in production —
     * `GEN_NOT_FOUND` on `POST /v1/reports/{reportKey}/run`, never reaching
     * `ipPricingRouter` at all — while the charges themselves were captured
     * correctly. Do not drop the segment again.
     */
    {
        operationId: 'runIpReport',
        method: 'POST',
        path: '/v1/ip/reports/{reportKey}/run',
        summary: 'Run one of the IP reporting framework\'s registered reports',
        version: 'v1',
        tags: ['IP Billing'],
        requestSchemaRef: 'runIpReportSchema',
        responseSchemaRef: 'IpReportResult',
        errorCodes: ['GEN_NOT_FOUND', 'GEN_VALIDATION_ERROR', 'AUTHZ_FORBIDDEN'],
    },
];
//# sourceMappingURL=ip-billing.operations.js.map